/**
 * @author    Labs64 <netlicensing@labs64.com>
 * @license   Apache-2.0
 * @link      https://netlicensing.io
 * @copyright 2017 Labs64 NetLicensing
 */
// entity factory
import defineEntity from '@/entities/defineEntity';

// types
import { PaymentMethodValues } from '@/types/constants/PaymentMethodEnum';
import { TransactionSourceValues } from '@/types/constants/TransactionSource';
import { TransactionStatusValues } from '@/types/constants/TransactionStatus';
import { LicenseTransactionJoinEntity } from '@/types/entities/LicenseTransactionJoin';
import { TransactionMethods, TransactionProps, TransactionEntity } from '@/types/entities/Transaction';

// utils
import { set, get } from '@/utils/helpers';
import serialize from '@/utils/serialize';

/**
 * Transaction entity used internally by NetLicensing.
 *
 * Properties visible via NetLicensing API:
 *
 * Unique number (across all products of a vendor) that identifies the transaction. This number is
 * always generated by NetLicensing.
 * @property string number
 *
 * always true for transactions
 * @property boolean active
 *
 * Status of transaction. "CANCELLED", "CLOSED", "PENDING".
 * @property string status
 *
 * "SHOP". AUTO transaction for internal use only.
 * @property string source
 *
 * grand total for SHOP transaction (see source).
 * @property number grandTotal
 *
 * discount for SHOP transaction (see source).
 * @property number discount
 *
 * specifies currency for money fields (grandTotal and discount). Check data types to discover which
 * @property string currency
 *
 * Date created. Optional.
 * @property string dateCreated
 *
 * Date closed. Optional.
 * @property string dateClosed
 */

const Transaction = function <T extends object>(
  properties: TransactionProps<T> = {} as TransactionProps<T>,
): TransactionEntity<T> {
  const props: TransactionProps = { ...properties };

  const methods: TransactionMethods = {
    setActive(this: void, active: boolean) {
      set(props, 'active', active);
    },

    getActive<D = undefined>(this: void, def?: D): boolean | D {
      return get(props, 'active', def) as boolean | D;
    },

    setNumber(this: void, number: string): void {
      set(props, 'number', number);
    },

    getNumber<D = undefined>(this: void, def?: D): string | D {
      return get(props, 'number', def) as string | D;
    },

    setStatus(this: void, status: TransactionStatusValues): void {
      set(props, 'status', status);
    },

    getStatus<D = undefined>(this: void, def?: D): TransactionStatusValues | D {
      return get(props, 'status', def) as TransactionStatusValues | D;
    },

    setSource(this: void, source: TransactionSourceValues): void {
      set(props, 'source', source);
    },
    getSource<D = undefined>(this: void, def?: D): TransactionSourceValues | D {
      return get(props, 'source', def) as TransactionSourceValues | D;
    },
    setGrandTotal(this: void, grandTotal: number): void {
      set(props, 'grandTotal', grandTotal);
    },
    getGrandTotal<D = undefined>(this: void, def?: D): number | D {
      return get(props, 'grandTotal', def) as number | D;
    },

    setDiscount(this: void, discount: number): void {
      set(props, 'discount', discount);
    },

    getDiscount<D = undefined>(this: void, def?: D): number | D {
      return get(props, 'discount', def) as number | D;
    },

    setCurrency(this: void, currency: string): void {
      set(props, 'currency', currency);
    },

    getCurrency<D = undefined>(this: void, def?: D): string | D {
      return get(props, 'currency', def) as string | D;
    },

    setDateCreated(this: void, dateCreated: Date): void {
      set(props, 'dateCreated', dateCreated);
    },

    getDateCreated<D = undefined>(this: void, def?: D): Date | D {
      return get(props, 'dateCreated', def) as Date | D;
    },

    setDateClosed(this: void, dateCreated: Date): void {
      set(props, 'dateClosed', dateCreated);
    },

    getDateClosed<D = undefined>(this: void, def?: D): Date | D {
      return get(props, 'dateClosed', def) as Date | D;
    },

    setPaymentMethod(this: void, paymentMethod: PaymentMethodValues): void {
      set(props, 'paymentMethod', paymentMethod);
    },

    getPaymentMethod<D = undefined>(this: void, def?: D): PaymentMethodValues | D {
      return get(props, 'paymentMethod', def) as PaymentMethodValues | D;
    },

    setLicenseTransactionJoins(this: void, joins: LicenseTransactionJoinEntity[]): void {
      set(props, 'licenseTransactionJoins', joins);
    },

    getLicenseTransactionJoins<D = undefined>(this: void, def?: D): LicenseTransactionJoinEntity[] | D {
      return get(props, 'licenseTransactionJoins', def) as LicenseTransactionJoinEntity[] | D;
    },

    serialize(this: void) {
      const clone: Record<string, unknown> = {...props};

      clone.datecreated = props.dateCreated;
      clone.dateclosed = props.dateClosed

      delete clone.dateCreated;
      delete clone.dateClosed;

      return serialize(clone, { ignore: ['licenseTransactionJoins', 'inUse'] });
    },
  };

  return defineEntity(props as TransactionProps<T>, methods, Transaction);
};

export default Transaction;
